import React from 'react';
import { Bot, User, Pencil, Wrench, CheckCircle, XCircle } from 'lucide-react';
import { ToolCallsGroup } from './ToolCallDisplay';
import { ThinkingBlock } from './ThinkingBlock';
import { MarkdownContent } from '../shared/MarkdownContent';
import { Message } from './hooks/useChatSession';

interface ChatMessageListProps {
  messages: Message[];
  isLoading: boolean;
  onEditMessage: (messageId: string) => void;
}

/**
 * Component for rendering the list of chat messages
 */
export function ChatMessageList({ messages, isLoading, onEditMessage }: ChatMessageListProps) {
  return (
    <>
      {messages.map((message) => {
        const isToolMessage = message.role === 'tool_use' || message.role === 'tool_result';
        return (
        <div
          key={message.id}
          className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''} group`}
        >
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            message.role === 'user' ? 'bg-primary-500/20'
            : isToolMessage ? 'bg-surface-600'
            : 'bg-surface-700'
          }`}>
            {message.role === 'user' ? (
              <User className="w-4 h-4 text-primary-400" />
            ) : message.role === 'tool_use' ? (
              <Wrench className="w-4 h-4 text-amber-400" />
            ) : message.role === 'tool_result' ? (
              message.content.startsWith('Error') || message.content.startsWith('error')
                ? <XCircle className="w-4 h-4 text-red-400" />
                : <CheckCircle className="w-4 h-4 text-success-400" />
            ) : (
              <Bot className="w-4 h-4 text-text-muted" />
            )}
          </div>
          <div className={`flex-1 max-w-[80%] ${message.role === 'user' ? 'text-right' : ''}`}>
            <div className={`inline-block rounded-lg px-3 py-2 text-sm ${
              message.role === 'user'
                ? 'bg-primary-500/20 text-text-primary'
                : isToolMessage
                ? 'bg-surface-700/50 text-text-secondary font-mono text-xs'
                : 'bg-surface-800 text-text-primary'
            }`}>
              {message.role === 'assistant' ? (
                <>
                  {message.thinking && <ThinkingBlock content={message.thinking} />}
                  <MarkdownContent content={message.content} />
                </>
              ) : isToolMessage ? (
                <details className="cursor-pointer">
                  <summary className="text-text-muted select-none">
                    {message.role === 'tool_use' ? 'Tool Call' : 'Tool Result'}
                    {message.content.length > 100 ? ` (${message.content.length} chars)` : ''}
                  </summary>
                  <pre className="mt-1 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                    {message.content}
                  </pre>
                </details>
              ) : (
                <p className="whitespace-pre-wrap">{message.content}</p>
              )}
              {/* Show tool calls */}
              {message.toolCalls && message.toolCalls.length > 0 && (
                <ToolCallsGroup
                  toolCalls={message.toolCalls}
                  results={message.toolResults || []}
                />
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-text-muted">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              {/* Edit button for user messages when not loading */}
              {message.role === 'user' && !isLoading && (
                <button
                  onClick={() => onEditMessage(message.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-text-primary hover:bg-surface-700 rounded transition-all"
                  title="Edit and resend"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>
        );
      })}
    </>
  );
}
